"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import {
  ChevronDown,
  LayoutDashboard,
  LogOut,
  Menu,
  Settings,
  UserRound,
  X,
} from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { track } from "@vercel/analytics";

import { LanguageSwitcher } from "@/components/i18n/LanguageSwitcher";
import { Link, usePathname } from "@/i18n/navigation";
import { normalizeLocale, type LandingLocale } from "@/i18n/routing";
import { initialsFromName, pastelAvatarColor } from "@/lib/avatarDisplay";
import {
  isDirectoryHandoffMenuId,
  marketplaceMenuHref,
} from "@/lib/marketplaceMenuHref";
import { reactWebAppHref, reactWebAuthHref, requestReactWebLogout } from "@/lib/reactWebAuthUrl";
import type { ViewerChromeSession } from "@/lib/viewerSession";

type MegaMenuSection = Readonly<{
  href: string;
  id: string;
  title: string;
  items: readonly {
    description?: string;
    href: string;
    label: string;
  }[];
}>;

type MegaMenuFooterCta = Readonly<{
  href: string;
  label: string;
}>;

type MegaMenuItem = Readonly<{
  footerCta?: MegaMenuFooterCta;
  href: string;
  id: string;
  introBody: string;
  introTitle: string;
  label: string;
  sections: readonly MegaMenuSection[];
}>;

const localeLabelKeys: Partial<Record<LandingLocale, string>> = {
  en: "switchToEnglish",
  es: "switchToSpanish",
  "pt-BR": "switchToPortugueseBrazil",
};

type SiteNavigationProps = Readonly<{
  viewerSession: ViewerChromeSession;
}>;

function internalHref(href: string): string {
  return href.startsWith("/") ? href : `/${href}`;
}

function megaMenuWidthClassName(sectionCount: number): string {
  if (sectionCount <= 1) return "w-[min(calc(100vw-2rem),48rem)]";
  if (sectionCount === 2) return "w-[min(calc(100vw-2rem),60rem)]";
  if (sectionCount === 3) return "w-[min(calc(100vw-2rem),70rem)]";
  return "w-[min(calc(100vw-2rem),78rem)]";
}

function megaMenuGridClassName(sectionCount: number): string {
  if (sectionCount <= 1) return "xl:grid-cols-1";
  if (sectionCount === 2) return "xl:grid-cols-2";
  if (sectionCount === 3) return "xl:grid-cols-3";
  return "xl:grid-cols-4";
}

export default function SiteNavigation({ viewerSession }: SiteNavigationProps) {
  const t = useTranslations("navigation");
  const locale = normalizeLocale(useLocale());
  const pathname = usePathname();
  const getLocaleLabel = (option: LandingLocale) => {
    const labelKey = localeLabelKeys[option];
    return labelKey ? t(labelKey) : option;
  };

  const megaMenus = (t.raw("megaMenus") as MegaMenuItem[]) ?? [];
  const marketplaceMenus = (t.raw("marketplaceMenus") as MegaMenuItem[]) ?? [];
  const allMegaMenus = megaMenus;

  const isActiveHref = (href: string) => {
    const basePath = internalHref(href).split("#")[0];
    return pathname === basePath || pathname.startsWith(`${basePath}/`);
  };

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null);
  const [mobileExpandedId, setMobileExpandedId] = useState<string | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement | null>(null);
  const navRef = useRef<HTMLElement | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!activeDropdownId) return undefined;

    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (!navRef.current?.contains(event.target as Node)) {
        setActiveDropdownId(null);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setActiveDropdownId(null);
      }
    };

    document.addEventListener("pointerdown", closeOnOutsidePointer);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePointer);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [activeDropdownId]);

  useEffect(() => (
    () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    }
  ), []);

  const closeMobile = () => setMobileMenuOpen(false);
  const openDropdown = (id: string) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveDropdownId(id);
  };
  const closeDropdown = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setActiveDropdownId(null);
  };
  const scheduleDropdownClose = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      closeTimeoutRef.current = null;
      setActiveDropdownId(null);
    }, 160);
  };

  const displayName = viewerSession.displayName || t("accountFallbackName");
  const fallbackInitials = initialsFromName(displayName);
  const fallbackAvatarColor = pastelAvatarColor(displayName);

  const accountMenuItems = [
    {
      href: reactWebAppHref(locale, "dashboard"),
      icon: LayoutDashboard,
      label: t("accountDashboard"),
    },
    {
      href: reactWebAppHref(locale, "my-profile"),
      icon: UserRound,
      label: t("accountMyProfile"),
    },
    {
      href: reactWebAppHref(locale, "settings"),
      icon: Settings,
      label: t("accountSettings"),
    },
  ] as const;

  const activeMegaMenu = allMegaMenus.find((item) => `mega:${item.id}` === activeDropdownId);
  const activeMegaMenuIsMarketplace = activeMegaMenu
    ? marketplaceMenus.some((item) => item.id === activeMegaMenu.id)
    : false;

  const renderInternalMenuLink = (
    href: string,
    label: string,
    className: string,
    onClick?: () => void,
    key?: string,
  ) => (
    <Link
      aria-current={isActiveHref(href) ? "page" : undefined}
      key={key ?? `${href}:${label}`}
      href={internalHref(href)}
      className={className}
      onClick={() => {
        track("public_navigation_click", { destination: href, label });
        onClick?.();
      }}
    >
      {label}
    </Link>
  );

  const renderMegaMenuLink = (
    item: MegaMenuSection["items"][number],
    className: string,
    key: string,
  ) => (
    <Link
      aria-current={isActiveHref(item.href) ? "page" : undefined}
      className={className}
      href={internalHref(item.href)}
      key={key}
      onClick={() => {
        track("public_navigation_click", {
          destination: item.href,
          label: item.label,
        });
        closeDropdown();
      }}
    >
      <span>{item.label}</span>
      {item.description ? (
        <span className="site-nav-menu-description mt-1 text-xs font-medium leading-5 text-slate-500">
          {item.description}
        </span>
      ) : null}
    </Link>
  );

  const renderTriggerContent = (label: string) => (
    <span className="site-nav-trigger-content">
      <span className="site-nav-trigger-label" lang={locale}>
        {label}
      </span>
      <ChevronDown aria-hidden="true" className="site-nav-trigger-chevron h-3.5 w-3.5 2xl:h-4 2xl:w-4" />
    </span>
  );

  const accountMenu = viewerSession.isAuthenticated ? (
    <div
      className="relative"
      ref={accountMenuRef}
      onMouseEnter={() => openDropdown("account")}
      onMouseLeave={scheduleDropdownClose}
    >
      <button
        type="button"
        className="inline-flex h-10 items-center gap-1 rounded-full bg-slate-50 px-2 text-slate-950 shadow-sm ring-1 ring-slate-200 transition hover:bg-white hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 2xl:h-11 2xl:gap-3 2xl:px-3 2xl:pr-4"
        aria-expanded={activeDropdownId === "account"}
        aria-haspopup="menu"
        aria-label={t("accountMenuLabel")}
        onClick={() => {
          if (activeDropdownId === "account") {
            closeDropdown();
          } else {
            openDropdown("account");
          }
        }}
      >
        <span
          className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-full text-sm font-bold text-slate-800 ring-2 ring-white"
          style={!viewerSession.avatarUrl ? { background: fallbackAvatarColor } : undefined}
          aria-hidden="true"
        >
          {viewerSession.avatarUrl ? (
            // Presentation-only cross-app avatar URL; it may come from Auth0 or a signed URL.
            // eslint-disable-next-line @next/next/no-img-element
            <img className="h-full w-full object-cover" src={viewerSession.avatarUrl} alt="" />
          ) : (
            fallbackInitials
          )}
        </span>
        <strong className="hidden max-w-[8rem] truncate text-sm font-bold 2xl:block">
          {displayName}
        </strong>
        <ChevronDown
          aria-hidden="true"
          className={`h-4 w-4 shrink-0 text-slate-600 transition ${
            activeDropdownId === "account" ? "rotate-180" : ""
          }`}
        />
      </button>

      {activeDropdownId === "account" ? (
        <div
          role="menu"
          aria-label={t("accountMenuLabel")}
          className="absolute right-0 top-[calc(100%+0.6rem)] z-[90] w-56 overflow-hidden rounded-lg border border-slate-200 bg-white py-2 text-sm shadow-xl ring-1 ring-slate-200"
        >
          {accountMenuItems.map(({ href, icon: Icon, label }) => (
            <a
              key={href}
              href={href}
              role="menuitem"
              className="flex items-center gap-3 px-4 py-3 font-semibold text-slate-700 transition hover:bg-slate-50 hover:text-primary"
              onClick={closeDropdown}
            >
              <Icon aria-hidden="true" className="h-4 w-4 text-slate-500" />
              {label}
            </a>
          ))}
          <div className="my-1 border-t border-slate-100" />
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-3 px-4 py-3 text-left font-semibold text-red-600 transition hover:bg-red-50"
            onClick={() => requestReactWebLogout(locale)}
          >
            <LogOut aria-hidden="true" className="h-4 w-4" />
            {t("accountLogout")}
          </button>
        </div>
      ) : null}
    </div>
  ) : null;

  return (
    <>
      <nav
        ref={navRef}
        className="site-header"
      >
        <div className="site-header__shell">
          <div className="site-header__row">
            <div
              className={`site-header__bar ${isScrolled ? "site-header__bar--compact" : ""}`}
            >
            <div className="site-header__brand">
              <Link
                href="/"
                className="site-header__brand-link logo-seesaw"
                onClick={closeMobile}
              >
                <Image
                  src="/brand/dronejob-header-wordmark.png"
                  alt={t("brandAlt")}
                  width={600}
                  height={126}
                  sizes="(min-width: 1536px) 220px, (min-width: 1366px) 150px, (min-width: 1280px) 130px, (min-width: 1180px) 115px, (min-width: 1112px) 105px, (min-width: 1024px) 92px, 210px"
                  className="transition-all duration-300"
                  priority
                />
              </Link>
            </div>

            <div className="site-header__nav site-header__nav--desktop">
              <div className="site-header__nav-group">
                {megaMenus.map((item) => (
                  <div
                    key={item.id}
                    className="site-header__menu-item"
                    onMouseEnter={() => openDropdown(`mega:${item.id}`)}
                    onMouseLeave={scheduleDropdownClose}
                  >
                    <button
                      type="button"
                      className={`site-header__nav-link site-header__nav-link--platform site-nav-trigger ${
                        isActiveHref(item.href) ? "site-header__nav-link--active" : ""
                      }`}
                      aria-current={isActiveHref(item.href) ? "page" : undefined}
                      aria-expanded={activeDropdownId === `mega:${item.id}`}
                      aria-haspopup="menu"
                      onClick={() => {
                        if (activeDropdownId === `mega:${item.id}`) {
                          closeDropdown();
                        } else {
                          openDropdown(`mega:${item.id}`);
                        }
                      }}
                    >
                      {renderTriggerContent(item.label)}
                    </button>
                  </div>
                ))}
              </div>

              <div className="site-header__divider" aria-hidden="true" />

              <div className="site-header__nav-group">
                {marketplaceMenus.map((item) => {
                  const className = `site-header__nav-link site-header__nav-link--marketplace site-nav-trigger ${
                    isActiveHref(item.href) && !isDirectoryHandoffMenuId(item.id)
                      ? "site-header__nav-link--active"
                      : ""
                  }`;
                  const onNavigate = () => {
                    track("public_navigation_click", {
                      destination: item.href,
                      label: item.label,
                    });
                    closeDropdown();
                  };
                  if (isDirectoryHandoffMenuId(item.id)) {
                    return (
                      <a
                        key={item.id}
                        href={marketplaceMenuHref(locale, item)}
                        className={className}
                        rel="noopener noreferrer"
                        onClick={onNavigate}
                      >
                        <span className="site-nav-trigger-label" lang={locale}>{item.label}</span>
                      </a>
                    );
                  }
                  return (
                    <Link
                      aria-current={isActiveHref(item.href) ? "page" : undefined}
                      key={item.id}
                      href={internalHref(item.href)}
                      className={className}
                      onClick={onNavigate}
                    >
                      <span className="site-nav-trigger-label" lang={locale}>{item.label}</span>
                    </Link>
                  );
                })}
              </div>

            </div>

            <div className="site-header__actions site-header__actions--desktop">
              {viewerSession.isAuthenticated ? (
                <div className="flex shrink-0 items-center">{accountMenu}</div>
              ) : (
                <>
                  <a
                    className="site-header__btn site-header__btn--login site-nav-action-trigger"
                    href={reactWebAuthHref(locale, "login")}
                  >
                    {t("login")}
                  </a>

                  <a
                    className="site-header__btn site-header__btn--start site-nav-action-trigger"
                    href={reactWebAuthHref(locale, "signup")}
                  >
                    {t("startFree.label")}
                  </a>
                </>
              )}
            </div>

            <button
              type="button"
              className="site-header__mobile-toggle"
              aria-label={mobileMenuOpen ? t("closeMenu") : t("openMenu")}
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen((o) => !o)}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
            </div>

            <LanguageSwitcher
              ariaLabel={t("language")}
              getLocaleLabel={getLocaleLabel}
              locale={locale}
              pathname={pathname}
              placement="navigation"
            />
          </div>
        </div>

        {activeMegaMenu ? (
          <div
            className={`absolute left-1/2 top-full z-[85] -translate-x-1/2 pt-3 ${
              activeMegaMenuIsMarketplace
                ? "w-[min(calc(100vw-2rem),32rem)]"
                : megaMenuWidthClassName(activeMegaMenu.sections.length)
            }`}
            onMouseEnter={() => openDropdown(`mega:${activeMegaMenu.id}`)}
            onMouseLeave={scheduleDropdownClose}
          >
            <div
              className="max-h-[calc(100vh-7rem)] overflow-x-hidden overflow-y-auto rounded-lg border border-slate-200 bg-white p-4 shadow-2xl ring-1 ring-slate-200"
              role="menu"
              aria-label={activeMegaMenu.label}
            >
              {activeMegaMenuIsMarketplace ? (
                <div className="grid gap-3">
                  <Link
                    href={internalHref(activeMegaMenu.href)}
                    className="rounded-lg bg-slate-50 p-4 text-slate-800 transition hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                    onClick={() => {
                      track("public_navigation_click", {
                        destination: activeMegaMenu.href,
                        label: activeMegaMenu.label,
                      });
                      closeDropdown();
                    }}
                  >
                    <p className="text-xs font-bold uppercase tracking-[0.08em] text-accent">
                      {activeMegaMenu.label}
                    </p>
                    <h2 className="mt-2 text-lg font-bold tracking-normal text-slate-950">
                      {activeMegaMenu.introTitle}
                    </h2>
                    <p className="mt-2 text-sm leading-6 text-slate-600">
                      {activeMegaMenu.introBody}
                    </p>
                  </Link>
                  <div className="grid gap-1">
                    {activeMegaMenu.sections.flatMap((section) => section.items).map((item) => renderMegaMenuLink(
                      item,
                      "rounded-md px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25",
                      `${activeMegaMenu.id}:${item.label}`,
                    ))}
                  </div>
                </div>
              ) : (
                <div className="grid gap-5 lg:grid-cols-[17rem_1fr]">
                  <Link
                    href={internalHref(activeMegaMenu.href)}
                    className="rounded-lg bg-primary p-5 text-white transition hover:bg-primary/95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                    onClick={() => {
                      track("public_navigation_click", {
                        destination: activeMegaMenu.href,
                        label: activeMegaMenu.label,
                      });
                      closeDropdown();
                    }}
                  >
                    <p className="text-sm font-bold uppercase text-white/70">{activeMegaMenu.label}</p>
                    <h2 className="mt-3 text-2xl font-bold tracking-normal">{activeMegaMenu.introTitle}</h2>
                    <p className="mt-3 text-sm leading-6 text-white/80">{activeMegaMenu.introBody}</p>
                    {activeMegaMenu.footerCta ? (
                      <span className="mt-5 inline-flex rounded-md bg-white px-4 py-2 text-sm font-bold text-primary">
                        {activeMegaMenu.footerCta.label}
                      </span>
                    ) : null}
                  </Link>
                  <div className={`grid min-w-0 gap-4 md:grid-cols-2 ${
                    megaMenuGridClassName(activeMegaMenu.sections.length)
                  }`}>
                    {activeMegaMenu.sections.map((section, sectionIndex) => (
                      <section className="min-w-0" key={`${section.id}:${sectionIndex}`}>
                        {section.title ? (
                          <h3 className="mb-2 text-xs font-bold uppercase tracking-[0.08em] text-slate-500">
                            <Link
                              className="rounded-sm transition hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25"
                              href={internalHref(section.href)}
                              onClick={closeDropdown}
                            >
                              {section.title}
                            </Link>
                          </h3>
                        ) : null}
                        <div className="space-y-1">
                          {section.items.map((item) => renderMegaMenuLink(
                            item,
                            "block rounded-md px-2 py-1.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25",
                            `${section.title}:${item.label}`,
                          ))}
                        </div>
                      </section>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : null}

        {mobileMenuOpen ? (
          <div className="max-h-[calc(100vh-5rem)] overflow-y-auto border-t border-gray-200 bg-white lg:hidden">
            <div className="space-y-3 px-4 py-4">
              {megaMenus.map((item) => {
                const expanded = mobileExpandedId === item.id;
                return (
                  <div className="rounded-lg border border-slate-200" key={item.id}>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left font-bold text-slate-800"
                      aria-expanded={expanded}
                      onClick={() => setMobileExpandedId(expanded ? null : item.id)}
                    >
                      {item.label}
                      <ChevronDown
                        aria-hidden="true"
                        className={`h-4 w-4 shrink-0 transition ${expanded ? "rotate-180" : ""}`}
                      />
                    </button>
                    {expanded ? (
                      <div className="border-t border-slate-100 px-3 py-3">
                        {renderInternalMenuLink(
                          item.href,
                          item.introTitle,
                          "mb-3 block rounded-md bg-primary px-3 py-3 text-sm font-semibold text-white",
                          closeMobile,
                        )}
                        <div className="space-y-4">
                          {item.sections.map((section, sectionIndex) => (
                            <section key={`${section.id}:${sectionIndex}`}>
                              {section.title ? (
                                <h3 className="mb-2 text-xs font-bold uppercase tracking-[0.08em] text-slate-500">
                                  <Link
                                    className="rounded-sm hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/25"
                                    href={internalHref(section.href)}
                                    onClick={closeMobile}
                                  >
                                    {section.title}
                                  </Link>
                                </h3>
                              ) : null}
                              <div className="grid gap-1">
                                {section.items.map((sectionItem) => renderInternalMenuLink(
                                  sectionItem.href,
                                  sectionItem.label,
                                  "block rounded-md px-2 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 hover:text-primary",
                                  closeMobile,
                                  `${section.title}:${sectionItem.label}`,
                                ))}
                              </div>
                            </section>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}

              <div className="space-y-1 rounded-lg border border-slate-200 p-2">
                {marketplaceMenus.map((item) => {
                  const className = "flex items-center justify-between rounded-md px-3 py-3 text-sm font-bold text-slate-800 hover:bg-slate-50 hover:text-primary";
                  if (isDirectoryHandoffMenuId(item.id)) {
                    return (
                      <a
                        key={`mobile-marketplace:${item.id}`}
                        href={marketplaceMenuHref(locale, item)}
                        className={className}
                        rel="noopener noreferrer"
                        onClick={() => {
                          track("public_navigation_click", {
                            destination: item.href,
                            label: item.label,
                          });
                          closeMobile();
                        }}
                      >
                        {item.label}
                      </a>
                    );
                  }
                  return renderInternalMenuLink(
                    item.href,
                    item.label,
                    className,
                    closeMobile,
                    `mobile-marketplace:${item.id}`,
                  );
                })}
              </div>

              {viewerSession.isAuthenticated ? (
                <div className="space-y-1 border-t border-gray-100 pt-3">
                  <div className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2">
                    <span
                      className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-full text-sm font-bold text-slate-800"
                      style={!viewerSession.avatarUrl ? { background: fallbackAvatarColor } : undefined}
                      aria-hidden="true"
                    >
                      {viewerSession.avatarUrl ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img className="h-full w-full object-cover" src={viewerSession.avatarUrl} alt="" />
                      ) : (
                        fallbackInitials
                      )}
                    </span>
                    <strong className="min-w-0 truncate text-sm text-slate-950">{displayName}</strong>
                  </div>
                  {accountMenuItems.map(({ href, icon: Icon, label }) => (
                    <a
                      key={href}
                      href={href}
                      className="flex items-center gap-3 rounded-lg px-3 py-2 text-left font-semibold text-gray-700 hover:bg-gray-50"
                      onClick={closeMobile}
                    >
                      <Icon aria-hidden="true" className="h-4 w-4 text-slate-500" />
                      {label}
                    </a>
                  ))}
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left font-semibold text-red-600 hover:bg-red-50"
                    onClick={() => requestReactWebLogout(locale)}
                  >
                    <LogOut aria-hidden="true" className="h-4 w-4" />
                    {t("accountLogout")}
                  </button>
                </div>
              ) : (
                <div className="space-y-3 border-t border-gray-100 pt-3">
                  <a
                    className="flex min-h-11 items-center justify-center rounded-md border border-primary px-4 text-sm font-bold text-primary hover:bg-primary hover:text-white"
                    href={reactWebAuthHref(locale, "login")}
                    onClick={closeMobile}
                  >
                    {t("login")}
                  </a>
                  <a
                    className="flex min-h-11 items-center justify-center rounded-md bg-accent px-4 text-sm font-bold text-white hover:bg-accent/90"
                    href={reactWebAuthHref(locale, "signup")}
                    onClick={closeMobile}
                  >
                    {t("startFree.label")}
                  </a>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </nav>
    </>
  );
}
